import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, Tabs, Table, Select, Space, Typography, Button, Tag , message } from "antd";
import Alert from "../components/GxAlert";
import { DownloadOutlined } from "@ant-design/icons";
import { getPeriods, getReportDebtors, getReportSalaryPayments, getReportProductionOutput } from "../api/client";
import ExportButton from "../components/ExportButton";
import GxTable from "../components/GxTable";
import GxTabs from "../components/GxTabs";
import { faNum } from "../utils/faNumberFormat";
import { downloadFile } from "../api/client";
import { getActiveYm, pickActivePeriod, rememberPeriod, setActiveYm } from "../utils/activeMonth";

const { Title, Text } = Typography;
const { Option } = Select;
const fa = (n) => faNum(Number(n || 0));

export default function ReportsPage() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  // لینک‌های قدیمی (?tab=debtors|salary) مستقیم به صفحهٔ مالی می‌روند
  useEffect(() => {
    const t = sp.get("tab");
    if (t === "debtors") navigate("/finance?tab=balances", { replace: true });
    if (t === "salary") navigate("/finance?tab=payments", { replace: true });
  }, [sp, navigate]);

  const [periods, setPeriods] = useState([]);
  const [periodId, setPeriodId] = useState(null);
  const [period, setPeriod] = useState(null);
  const [debtors, setDebtors] = useState([]);
  const [salary, setSalary] = useState({ rows: [], total_payable: 0 });
  const [prod, setProd] = useState([]);

  useEffect(() => { getPeriods().then(r => { setPeriods(r.data); const p = pickActivePeriod(r.data); if (p) { setPeriodId(p.id); setPeriod(p); } }); }, []);
  useEffect(() => {
    if (!periodId) return;
    setPeriod(periods.find(p => p.id === periodId));
    getReportDebtors(periodId).then(r => setDebtors(r.data.rows || []));
    getReportSalaryPayments(periodId).then(r => setSalary(r.data));
    getReportProductionOutput().then(r => setProd(r.data.rows || []));
  }, [periodId]); // eslint-disable-line

  const ym = period ? { year: period.year, month: period.month } : {};
  const periodSelector = (
    <Select value={periodId} onChange={(v) => { setPeriodId(v); rememberPeriod(periods, v); }} style={{ width: 130 }}>
      {periods.map(p => <Option key={p.id} value={p.id}>{p.year}/{String(p.month).padStart(2, "0")}</Option>)}
    </Select>);
  // دانلودِ احرازشده — لینکِ خام ۴۰۱ می‌داد
  const dl = (url, params) => downloadFile(url, "export.xlsx", { params })
    .catch(() => message.error("خطا در دانلودِ خروجی"));

  const tabs = [
    // «بدهکاران» و «پرداخت حقوق» به صفحهٔ مالی منتقل شدند تا یک تعریفِ واحد
    // داشته باشند؛ قبلاً سه لیستِ بدهکار با سه عددِ متفاوت وجود داشت.
    { key: "moved", label: "بدهکاران و پرداخت حقوق", children: (
      <Card title="این گزارش‌ها به «مالی پرسنل» منتقل شدند">
        <Alert type="info" showIcon style={{ marginBottom: 12 }}
          message="حالا یک جدولِ واحد برای مانده و پرداخت وجود دارد"
          description="قبلاً سه لیستِ بدهکار در سه صفحه بود که با هم نمی‌خواندند. حالا همه از یک محاسبه می‌آیند." />
        <Space wrap>
          <Button type="primary" onClick={() => navigate("/finance?tab=balances")}>
            ماندهٔ حساب پرسنل
          </Button>
          <Button onClick={() => navigate("/finance?tab=payments")}>
            پرداخت حقوق دوره
          </Button>
        </Space>
      </Card>) },
    { key: "advances", label: "لیست مساعده", children: (
      <Card title="مساعده‌ها" extra={<Space>{periodSelector}
        <Button icon={<DownloadOutlined />} onClick={() => dl("/api/v1/export/advances", ym)}>اکسل</Button></Space>}>
        <Text type="secondary">خروجی اکسل مساعده‌های ماه از دکمهٔ بالا.</Text>
      </Card>) },
    { key: "production", label: "گزارش تولید", children: (
      <Card title="خروجی تولید (جین/دانه)" extra={
        <Button icon={<DownloadOutlined />} onClick={() => dl("/api/v1/export/production-summary", ym)}>اکسل خلاصه</Button>}>
        <GxTable size="small" dataSource={prod} rowKey="order_id" pagination={{ pageSize: 20 }} scroll={{ x: "max-content" }}
          columns={[
            { title: "سری", dataIndex: "order_number" },
            { title: "کد برش", dataIndex: "cut_code", render: v => v || "—" },
            { title: "محصول", dataIndex: "product_type" },
            { title: "دوخت", dataIndex: "entered", render: fa },
            { title: "QC2", dataIndex: "qc2_jin_text" },
            { title: "بسته‌بندی", dataIndex: "packaging_jin_text" },
            { title: "کل دانه", dataIndex: "total_pieces", render: fa },
          ]} />
      </Card>) },
    { key: "insurance", label: "بیمه", children: (
      <Card title="گزارش بیمه" extra={<Space>{periodSelector}
        <Button icon={<DownloadOutlined />} onClick={() => dl("/api/v1/export/insurance", { period_id: periodId })}>اکسل</Button></Space>}>
        <Text type="secondary">خروجی اکسل بیمهٔ دوره از دکمهٔ بالا (حق بیمه + پرداختی بیمه‌ای).</Text>
      </Card>) },
  ];

  return (
    <div>
      <Title level={5} style={{ marginBottom: 16 }}>گزارش‌ها</Title>
      <GxTabs urlKey="tab" defaultActiveKey="debtors" items={tabs} />
    </div>
  );
}
